import { PostInfo } from '../../../../components/global/PostList/PostCard';
import { PostListItem } from '../../../../queries/homePage/postList';
import { getVideoTypes, isVideoPost, VideoType } from '../../domain/videoTypes';
import { fetchPostList } from '../../../../services/post';
import { ApolloClient } from '@apollo/client';
import { getContentPagePath } from '../../routing/urlGenerator';
import { PropertyType } from '../../domain/propertyTypes';
import { PostSubcategory } from '../../domain/postCategories';
import { HeroVideoInfo } from '../../../../queries/homePage/heroVideo';
import { fetchHeroVideo } from '../../../../services/heroVideo';
import { SearchParams } from '../../../../pages/searchResultPage/SearchResultPage';
import {
  fetchSearchPosts,
  fetchSearchPostsFreeText,
} from '../../../../services/search';

export type Content =
  | {
      type: 'post';
      content: PostInfo;
    }
  | {
      type: 'ad';
      content: null;
    }
  | {
      type: 'loading-post';
      content: null;
    };

export function getLoadingPostsWithAds(
  count: number,
  offset: number,
  shouldPutAd: (index: number) => boolean,
): Content[] {
  const result: Content[] = [];
  for (const index of Array(count).keys()) {
    const overallIndex = index + offset;
    if (shouldPutAd(overallIndex)) {
      result.push({
        type: 'ad' as const,
        content: null,
      });
    } else {
      result.push({ type: 'loading-post', content: null });
    }
  }

  return result;
}

export async function fetchPostsWithAds(
  client: ApolloClient<unknown>,
  count: number,
  offset: number,
  options: {
    propertyType?: PropertyType;
    postCategory?: PostSubcategory[];
    videoType?: VideoType[];
    tag?: string;
    searchParams?: SearchParams;
    freeText?: string;
  },
  shouldPutAd: (index: number) => boolean,
  excludePostIds: number[],
): Promise<{ contents: Content[]; hasMore: boolean }> {
  if (!count) {
    return {
      contents: [],
      hasMore: false,
    };
  }

  const numOfPostsNeeded = [...Array(count).keys()].filter(
    (index) => !shouldPutAd(index + offset),
  ).length;

  const postOffset = [...Array(offset).keys()].filter(
    (index) => !shouldPutAd(index),
  ).length;

  let postList = {
    items: [] as unknown[],
    totalCount: 0 as number,
  };

  if (options.freeText) {
    const data = await fetchSearchPostsFreeText(client, {
      postCategory: options.postCategory,
      videoType: options.videoType,
      freeText: options.freeText,
      offset: postOffset,
      perPage: numOfPostsNeeded,
    });
    postList = data.posts;
  } else if (options.searchParams) {
    const data = await fetchSearchPosts(client, {
      postCategory: options.postCategory,
      videoType: options.videoType,
      termsTaxonomy: options.searchParams.termsTaxonomy,
      termsSlug: options.searchParams.termsSlug,
      projectSlug: options.searchParams.projectSlug,
      offset: postOffset,
      perPage: numOfPostsNeeded,
    });
    postList = data.posts;
  } else {
    postList = await fetchPostList(client, {
      postCategory: options.postCategory,
      propertyType: options.propertyType,
      videoType: options.videoType,
      termsTaxonomy: options.tag ? 'tag' : null,
      termsSlug: options.tag ? options.tag : null,
      exclude: excludePostIds,
      offset: postOffset,
      perPage: numOfPostsNeeded,
    });
  }

  const postInfoList = postList.items
    .map(mapPostRawToPostInfo)
    .map((postInfo) => ({
      type: 'post' as const,
      content: postInfo,
    }));

  const result: Content[] = [];
  for (const index of Array(count).keys()) {
    const overallIndex = index + offset;
    if (shouldPutAd(overallIndex)) {
      result.push({
        type: 'ad' as const,
        content: null,
      });
    } else {
      const postInfo = postInfoList.shift();
      if (postInfo) {
        result.push(postInfo);
      } else {
        break;
      }
    }
  }

  const hasMore = postList.totalCount > offset + count;
  return { contents: result, hasMore };
}

export async function fetchPosts(
  client: ApolloClient<unknown>,
  count: number,
  offset: number,
  options: {
    propertyType?: PropertyType;
    postCategory?: PostSubcategory[];
    videoType?: VideoType[];
    tag?: string;
  },
  excludePostIds: number[],
): Promise<{ contents: Content[]; hasMore: boolean }> {
  if (!count) {
    return {
      contents: [],
      hasMore: false,
    };
  }

  const postList = await fetchPostList(client, {
    exclude: excludePostIds,
    offset,
    perPage: count,
    postCategory: options.postCategory,
    propertyType: options.propertyType,
    videoType: options.videoType,
    termsTaxonomy: options.tag ? 'tag' : null,
    termsSlug: options.tag ? options.tag : null,
  });

  const postInfoList = postList.items
    .map(mapPostRawToPostInfo)
    .map((postInfo) => ({ type: 'post' as const, content: postInfo }));
  const hasMore = postList.totalCount > offset + count;
  return { contents: postInfoList, hasMore };
}

export function mapPostRawToPostInfo(data: PostListItem): PostInfo {
  const { videoType } = data;
  const isVideo = isVideoPost(videoType);
  const imageUrl: string = data.featuredImage.url || '';
  return {
    authorName: data.author.name,
    category: data.postCategory,
    isVideo,
    link: getContentPagePath({
      propertyType: data.propertyType,
      title: data.title,
      postCategory: data.postCategory,
      link: data.link,
    }),
    title: data.title,
    imageUrl,
    id: data.id,
    videoType,
    tags: data.tags,
    project: data.project,
  };
}

export async function fetchVideos(
  client: ApolloClient<unknown>,
  featureVideosRequirement: number,
  options: {
    propertyType?: PropertyType;
    postCategory?: PostSubcategory[];
    tag?: string;
  },
): Promise<{
  featuredVideos: Content[];
  heroVideo: HeroVideoInfo;
}> {
  if (featureVideosRequirement <= 0) {
    return { featuredVideos: [], heroVideo: null };
  }

  const [heroVideo, videos] = await Promise.all([
    fetchHeroVideo(client, {
      postCategory: options.postCategory,
      propertyType: options.propertyType,
      tag: options.tag,
    }),
    fetchPostList(client, {
      perPage: featureVideosRequirement + 1,
      videoType: getVideoTypes(),
      postCategory: options.postCategory,
      propertyType: options.propertyType,
      termsTaxonomy: options.tag ? 'tag' : null,
      termsSlug: options.tag ? options.tag : null,
    }),
  ]);

  const featuredVideos = excludeHeroVideo(
    heroVideo?.id,
    videos?.items,
    featureVideosRequirement,
  )
    .map(mapPostRawToPostInfo)
    .map((postInfo) => ({ type: 'post' as const, content: postInfo }));

  return { featuredVideos, heroVideo };
}

export const excludeHeroVideo = (
  heroVideoId: number,
  videos: PostListItem[],
  numberOfItems: number,
): PostListItem[] => {
  return videos
    .filter((video) => video?.id !== heroVideoId)
    .slice(0, numberOfItems);
};
